import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, Tag, Select, Card, Typography, Button, Modal, Form, InputNumber, message, Empty } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import dayjs from '_helpers/datetime';
import { MongoRef } from './MongoRef';

const { Text, Title } = Typography;
const { Option } = Select;

export { AcademicTab };

// Học vụ (Academic) tab of the admin panel.
//   1. Học kỳ (Terms)       — read-only list. GET /api/terms is the only term endpoint that
//      exists server-side (see server/routers/term.js) — there is no create/edit/delete route,
//      so this section is intentionally view-only. That's a gap, not an oversight here.
//   2. Bảng điểm (Scores)   — pick a class, view every member's scores for that class, edit a
//      single score inline via PUT /api/scores/update/:scoreId.
//   3. Điểm danh (Attendance) — same class picker, read-only session list (lesson name, date,
//      number of attendance records).
function AcademicTab() {
    const fetchWrapper = useFetchWrapper();

    // ---- Terms (read-only) ----
    const [terms, setTerms] = useState([]);
    const [termsLoading, setTermsLoading] = useState(false);

    // ---- Classes (shared picker for the scores + attendance sections below) ----
    const [classes, setClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);
    const [classId, setClassId] = useState(null);

    // ---- Scores ----
    const [scoreTables, setScoreTables] = useState([]);
    const [scoresLoading, setScoresLoading] = useState(false);
    const [editRow, setEditRow] = useState(null); // { scoreId, studentName, subjectName, scoreType, score }
    const [editForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // ---- Attendance ----
    const [sessions, setSessions] = useState([]);
    const [attLoading, setAttLoading] = useState(false);

    const loadTerms = useCallback(async () => {
        setTermsLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/terms', null, null);
            const json = await res.json();
            if (json.status === 'Success') setTerms(Array.isArray(json.message) ? json.message : []);
            else message.error(typeof json.message === 'string' ? json.message : 'Không tải được danh sách học kỳ');
        } catch (e) {
            message.error('Không tải được danh sách học kỳ');
        }
        setTermsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadClasses = useCallback(async () => {
        setClassesLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/manager/classes', null, null);
            const json = await res.json();
            if (json.status === 'Success') setClasses(Array.isArray(json.message) ? json.message : []);
            else message.error(typeof json.message === 'string' ? json.message : 'Không tải được danh sách lớp');
        } catch (e) {
            message.error('Không tải được danh sách lớp');
        }
        setClassesLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadTerms(); loadClasses(); }, [loadTerms, loadClasses]);

    const loadScores = useCallback(async (cid) => {
        if (!cid) { setScoreTables([]); return; }
        setScoresLoading(true);
        try {
            const res  = await fetchWrapper.get(`/api/classes/${cid}/members/scores`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                setScoreTables(Array.isArray(json.message) ? json.message : []);
            } else {
                setScoreTables([]);
                message.error(typeof json.message === 'string' ? json.message : 'Không tải được bảng điểm');
            }
        } catch (e) {
            setScoreTables([]);
            message.error('Không tải được bảng điểm');
        }
        setScoresLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAttendance = useCallback(async (cid) => {
        if (!cid) { setSessions([]); return; }
        setAttLoading(true);
        try {
            const res  = await fetchWrapper.get(`/api/classes/${cid}/attendance`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                setSessions(Array.isArray(json.message) ? json.message : []);
            } else {
                setSessions([]);
                message.error(typeof json.message === 'string' ? json.message : 'Không tải được điểm danh');
            }
        } catch (e) {
            setSessions([]);
            message.error('Không tải được điểm danh');
        }
        setAttLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handlePickClass(cid) {
        setClassId(cid || null);
        loadScores(cid);
        loadAttendance(cid);
    }

    // fGetScoresClassByClassId returns one ScoresTable-shaped object per class member:
    //   { user_ref: {name, student_id, ...}, scores: [{ _id, score, score_type, subject: {subject_name, subject_code}, ... }] }
    // Flatten to one row per (student, score) for a plain table.
    const scoreRows = useMemo(() => {
        const rows = [];
        for (const table of scoreTables) {
            const student = table.user_ref || {};
            for (const s of (table.scores || [])) {
                rows.push({
                    key:         s._id,
                    scoreId:     s._id,
                    studentName: student.name || '—',
                    studentId:   student.student_id || '—',
                    subjectName: (s.subject && (s.subject.subject_name || s.subject.subject_code)) || '—',
                    subjectCode: (s.subject && s.subject.subject_code) || '',
                    scoreType:   s.score_type || '—',
                    score:       s.score,
                });
            }
        }
        return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoreTables]);

    function openEdit(row) {
        editForm.setFieldsValue({ score: row.score });
        setEditRow(row);
    }

    async function handleSaveScore() {
        let values;
        try { values = await editForm.validateFields(); } catch { return; }
        setSaving(true);
        try {
            const res  = await fetchWrapper.put(
                `/api/scores/update/${editRow.scoreId}`,
                'application/json',
                JSON.stringify({ score: values.score })
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã cập nhật điểm');
                setEditRow(null);
                await loadScores(classId);
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Cập nhật điểm thất bại');
            }
        } catch (e) {
            message.error('Cập nhật điểm thất bại');
        }
        setSaving(false);
    }

    const termColumns = [
        { title: 'Mã học kỳ', dataIndex: 'term_id', key: 'term_id', width: 140 },
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r._id} collection="terms" /> },
        { title: 'Tên học kỳ', dataIndex: 'name', key: 'name', render: v => v || <Text type="secondary">—</Text> },
        {
            title: 'Bắt đầu', dataIndex: 'start_date', key: 'start_date', width: 120,
            render: v => v ? dayjs(v).format('DD/MM/YYYY') : <Text type="secondary">—</Text>,
        },
        {
            title: 'Kết thúc', dataIndex: 'end_date', key: 'end_date', width: 120,
            render: v => v ? dayjs(v).format('DD/MM/YYYY') : <Text type="secondary">—</Text>,
        },
        {
            title: 'Trạng thái', dataIndex: 'is_current', key: 'is_current', width: 140,
            render: v => v ? <Tag color="green">Đang diễn ra</Tag> : <Tag>Đã kết thúc</Tag>,
        },
    ];

    const scoreColumns = [
        {
            title: 'Học sinh', key: 'student',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.studentName}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-4, #999)' }}>{r.studentId}</div>
                </div>
            ),
        },
        {
            title: 'Môn', key: 'subject',
            render: (_, r) => r.subjectCode ? `${r.subjectName} (${r.subjectCode})` : r.subjectName,
        },
        { title: 'Loại điểm', dataIndex: 'scoreType', key: 'scoreType', width: 130 },
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r.scoreId} collection="scores" /> },
        {
            title: 'Điểm', dataIndex: 'score', key: 'score', width: 110,
            render: v => (typeof v === 'number' ? v : <Text type="secondary">—</Text>),
        },
        {
            title: '', key: 'actions', width: 60,
            render: (_, r) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />,
        },
    ];

    const attendanceColumns = [
        { title: 'Buổi học', dataIndex: 'lesson_name', key: 'lesson_name', render: v => v || <Text type="secondary">—</Text> },
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r._id} collection="attendancesessions" /> },
        {
            title: 'Ngày', dataIndex: 'date', key: 'date', width: 130,
            render: v => v ? dayjs(v).format('DD/MM/YYYY') : <Text type="secondary">—</Text>,
        },
        {
            title: 'Số lượt điểm danh', key: 'records', width: 160,
            render: (_, r) => (r.records || []).length,
        },
        {
            title: 'Trạng thái', key: 'status', width: 130,
            render: (_, r) => r.cancelled ? <Tag color="red">Đã hủy</Tag> : (r.held ? <Tag color="green">Đã dạy</Tag> : <Tag>Chưa xác nhận</Tag>),
        },
    ];

    return (
        <div className="soft-in">
            <Title level={3} style={{ marginBottom: 16 }}>Học vụ</Title>

            <Card title="Học kỳ" style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Chỉ xem — hệ thống hiện chưa có API tạo/sửa/xóa học kỳ.
                </Text>
                <Table
                    columns={termColumns}
                    dataSource={terms.map(t => ({ ...t, key: t._id || t.term_id }))}
                    loading={termsLoading}
                    pagination={false}
                    locale={{ emptyText: <Empty description="Chưa có học kỳ" /> }}
                />
            </Card>

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Text strong>Chọn lớp:</Text>
                <Select
                    showSearch
                    allowClear
                    placeholder="Chọn lớp để xem bảng điểm / điểm danh"
                    style={{ minWidth: 280 }}
                    loading={classesLoading}
                    value={classId}
                    onChange={handlePickClass}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        (option.children || '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {classes.map(c => (
                        <Option key={c.class_id} value={c.class_id}>
                            {c.class_name} ({c.class_id})
                        </Option>
                    ))}
                </Select>
            </div>

            <Card title="Bảng điểm theo lớp" style={{ marginBottom: 16 }}>
                {!classId
                    ? <Empty description="Chọn lớp ở trên để xem bảng điểm" />
                    : (
                        <Table
                            columns={scoreColumns}
                            dataSource={scoreRows}
                            loading={scoresLoading}
                            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                            locale={{ emptyText: <Empty description="Lớp này chưa có điểm" /> }}
                        />
                    )}
            </Card>

            <Card title="Điểm danh theo lớp">
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Chỉ xem — chỉnh sửa điểm danh thực hiện ở trang Điểm danh của lớp.
                </Text>
                {!classId
                    ? <Empty description="Chọn lớp ở trên để xem điểm danh" />
                    : (
                        <Table
                            columns={attendanceColumns}
                            dataSource={sessions.map(s => ({ ...s, key: s._id }))}
                            loading={attLoading}
                            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                            locale={{ emptyText: <Empty description="Lớp này chưa có buổi học nào" /> }}
                        />
                    )}
            </Card>

            <Modal
                title={editRow ? `Sửa điểm — ${editRow.studentName} (${editRow.subjectName}, ${editRow.scoreType})` : 'Sửa điểm'}
                visible={!!editRow}
                onCancel={() => setEditRow(null)}
                onOk={handleSaveScore}
                confirmLoading={saving}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item
                        name="score"
                        label="Điểm (0–10)"
                        rules={[
                            { required: true, message: 'Nhập điểm' },
                            { type: 'number', min: 0, max: 10, message: 'Điểm phải từ 0 đến 10' },
                        ]}
                    >
                        <InputNumber min={0} max={10} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
